<template>
    <el-form-item
        v-if="![
            'application.applicants.0.aai.expenses_shared',
            'application.applicants.0.aai.is_sharing_dependents',
            'application.applicants.0.aai.is_sharing_addresses',
        ].includes(path)"
        :rules="{ required: isFieldRequired, validator: validateField, trigger: 'change' }"
        :label="field.label"
        :prop="fieldProp(field)"
        slot="reference"
    >
        <span slot="label">
            {{ field.label }}
            <span v-if="!_.isEmpty(field.content)" class="fin-relative fin-z-50 fin-ml-1"
                :class="isFrontend ? 'fin-hidden' : 'fin-hidden 2xl:fin-inline-flex'">
                <el-popover
                    placement="top-start"
                    :title="field.label"
                    width="200"
                    trigger="hover"
                    :content="field.content"
                    :disabled="_.isEmpty(field.content)"
                    popper-class="app-form-field-popover"
                    >
                    <i slot="reference" class="fas fa-circle-info fin-ml-2 fin-text-warning"></i>
                </el-popover>
            </span>
        </span>

        <div v-if="!_.isEmpty(field.content)" class="content fin-flex fin-w-full"
            :class="isFrontend ? '' : '2xl:fin-hidden'">
            {{ field.content }}
        </div>

        <template v-if="fixedData">
            <el-input v-model="fixedDataValue" disabled></el-input>
        </template>

        <template v-else-if="isRemote">
            <el-select
                class="fin-w-full"
                v-model="selectValue"
                :placeholder="_.get(field, 'placeholder', '')"
                filterable
                clearable
                @focus="remoteCall(true)"
                :loading="remoteLoading"
                :disabled="isDisabled(field)"
                :multiple="_.get(field, 'options.multiSelect', false)"
            >
                <template v-if="hasGroups">
                    <el-option-group v-for="(group, groupKey) in field.options.choices" :key="groupKey" :label="group.label">
                        <el-option
                            v-for="(item, itemKey) in group.options"
                            :key="groupKey + itemKey"
                            :label="item.label"
                            :value="String(item.value)"
                            :disabled="isOptionDisabled(field, item)"
                        ></el-option>
                    </el-option-group>
                </template>
                <template v-else>
                    <el-option
                        v-for="(item, index) in remoteOptions"
                        :key="index"
                        :label="item.label"
                        :value="String(item.value)"
                        :disabled="isOptionDisabled(field, item)"
                    ></el-option>
                </template>
            </el-select>
        </template>

        <template v-else>
            <el-select
                v-model="selectValue"
                class="fin-w-full"
                @focus="setCurrentField(field)"
                :placeholder="field.placeholder"
                clearable
                filterable
                :disabled="isDisabled(field)"
                :multiple="_.get(field, 'options.multiSelect', false)"
            >
                <template v-if="hasGroups">
                    <el-option-group v-for="(group, groupKey) in field.options.choices" :key="groupKey" :label="group.label">
                        <el-option
                            v-for="(item, itemKey) in group.options"
                            :key="groupKey + itemKey"
                            :label="item.label"
                            :value="String(item.value)"
                            :disabled="isOptionDisabled(field, item)"
                        ></el-option>
                    </el-option-group>
                </template>
                <template v-else>
                    <el-option
                        v-for="(item, index) in field.options.choices"
                        :key="index"
                        :label="item.label"
                        :value="String(item.value)"
                        :disabled="isOptionDisabled(field, item)"
                    ></el-option>
                </template>
            </el-select>
        </template>
    </el-form-item>
</template>

<script>
import formMixin from "@tenant/app-form/mixins/formMixin"
import { mapGetters } from "vuex"

export default {
    name: "SelectField",

    mixins: [formMixin],

    props: {
        field: {
            required: true,
            type: [Object, Array],
        },
        fieldKey: {
            required: true,
            type: Number,
        },
        path: {
            required: true,
            type: [String, Number],
        },
        value: {
            required: false,
        },
        formStore: {
            required: false,
            type: String,
            default: "formStore"
        },
    },

    data() {
        return {
            selectValue: null,
            remoteOptions: [],
            remoteLoading: false,
            fixedData: false,
            fixedDataValue: null,
        }
    },

    computed: {
        isFieldRequired() {
            return this.field.isRequired
        },
        hasGroups() {
            // get the first choice and see if it has groups
            let firstChoice = _.get(this.field, 'options.choices.0')
            if (_.has(firstChoice, "options", false)) {
                return true
            }
            return false
        },
        isRemote() {
            return (
                _.isString(this.field.options.endpoint) &&
                !_.isEmpty(this.field.options.endpoint) &&
                !_.isNull(this.field.options.endpoint)
            )
        },
        isMulti() {
            return this.field.options.multiSelect ? true : false
        }
    },

    mounted() {
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, this.path)
    },

    methods: {
        isDisabled(field) {
            // disabled the still owner select if the address is the first address
            if (this.path.includes("addresses.0.still_owner")) {
                return true
            }

            if (field.field_mapping_parent == "employment_histories") {
                if (field.field_mapping == "is_current") {
                    if (this.path.includes("employment_histories.0")) {
                        return true
                    }
                }
            }

            if (field.field_mapping_parent == "aai.asset_liabilities") {
                let parentPath = this.path.replace(`.${field.field_mapping}`, "")
                let parentPathData = _.get(this.formData, parentPath, false)

                if (parentPathData) {
                    if (field.field_mapping == "item") {
                        if (["1", "3"].includes(parentPathData.item)) {
                            this.selectValue = this.value
                            this.fixedData = true
                            if (parentPathData.item == "1") {
                                this.fixedDataValue = "Home"
                            } else if (parentPathData.item == "3") {
                                this.fixedDataValue = "Investment Property"
                            }
                            return true
                        }
                    }

                    if (field.field_mapping == "ownedOutright") {
                        if (["1", "3"].includes(parentPathData.item)) {
                            this.selectValue = this.value
                            this.fixedData = true
                            if (this.selectValue == "0") {
                                this.fixedDataValue = "No"
                            } else if (this.selectValue == "1") {
                                this.fixedDataValue = "Yes"
                            }
                            return true
                        }
                    }

                    if (field.field_mapping == "totalMonths") {
                        if (["1", "3"].includes(parentPathData.item)) {
                            this.selectValue = this.value
                            this.fixedData = true
                            this.fixedDataValue = `${this.selectValue} Months`
                            return true
                        }
                    }

                    if (field.field_mapping == "financier") {
                        if (["1", "3"].includes(parentPathData.item)) {
                            this.selectValue = this.value
                            this.fixedData = true
                            this.fixedDataValue = this.selectValue
                            return true
                        }
                    }

                    if (field.field_mapping == "frequency") {
                        if (["1", "3"].includes(parentPathData.item)) {
                            this.selectValue = this.value
                            this.fixedData = true
                            this.remoteCall(true)
                            let choices = field.options.choices.find((choice) => choice.value == this.selectValue)
                            this.fixedDataValue = _.get(choices, "label", this.selectValue)
                            return true
                        }
                    }
                }
            }

            if (field.field_mapping_parent == "aai.other_income") {
                if (field.field_mapping == "label") {
                    if (this.value == "Investment Property Income") {
                        this.fixedData = true
                        this.selectValue = this.value
                        this.fixedDataValue = this.selectValue
                        return true
                    }
                }
                if (this.field.field_mapping == "frequency") {
                    let parentPath = this.path.replace(`.${this.field.field_mapping}`, "")
                    let parentPathData = _.get(this.formData, parentPath, false)
                    if (parentPathData && parentPathData.label == "Investment Property Income") {
                        this.fixedData = true
                        this.selectValue = this.value
                        let frequencies = [
                            { label: "Weekly", value: "4" },
                            { label: "Fortnightly", value: "2" },
                            { label: "Monthly", value: "1" },
                            { label: "Quarterly", value: "0.333333333" },
                            { label: "Annual", value: "0.083333333" }
                        ]
                        let frequency = frequencies.find(frequency => Number(frequency.value) == Number(this.value))
                        this.fixedDataValue = frequency.label
                        return true
                    }
                }
            }

            return false
        },
        isOptionDisabled(field, item) {
            if (
                ["aai.expenses_general", "aai.expenses_discretionary", "aai.expenses_transport", "aai.expenses_other"].includes(
                    field.field_mapping_parent
                ) &&
                field.field_mapping == "label"
            ) {
                let split = field.field_mapping_parent.split(".")[1]
                let expensesPath = String(this.path).split(split)[0] + split
                let expenses = _.get(this.formData, expensesPath, false)
                return expenses.some((expense) => expense.label == item.label || expense.label == item.value)
            }
            return false
        },
        validateField(rule, value, callback) {
            if (!this.selectValue && this.isFieldRequired) {
                callback(new Error(`${this.field.label} is Required`))
            } else {
                callback()
            }
        },
        remoteCall(action, currentValue) {
            currentValue = typeof currentValue !== "undefined" && currentValue ? currentValue : false
            if (action) this.setCurrentField(this.field)
            if (_.size(this.remoteOptions) <= 1) {
                this.remoteLoading = true

                let auth
                if (this.isFrontend) {
                    auth = {}
                } else {
                    auth = {
                        headers: {
                            Authorization: `Bearer ${this.core.token}`,
                        },
                    }
                }

                axios
                    .post(
                        `/api/remote-resource/${this.field.options.endpoint}`,
                        {
                            currentValue: currentValue,
                        },
                        auth
                    )
                    .then((res) => {
                        this.remoteOptions = res.data

                        switch (this.field.field_mapping_parent) {
                            case "asset_liabilities":
                                switch (this.field.field_mapping) {
                                    case "item":
                                        this.remoteOptions = res.data.filter((option) => {
                                            if (
                                                option.application_type_id.includes(this.core.application.application_type_id) &&
                                                !["HOME_PROPERTY", "INVESTMENT_PROPERTY"].includes(option.key)
                                            ) {
                                                return option
                                            }
                                        })
                                        break
                                    case "financier":
                                        this.remoteOptions = res.data.map((option) => {
                                            return {
                                                label: option.label,
                                                value: option.label,
                                            }
                                        })
                                        break
                                }
                                break
                            case "aai.asset_liabilities":
                                switch (this.field.field_mapping) {
                                    case "item":
                                        this.remoteOptions = res.data.filter((option) => {
                                            if (
                                                option.application_type_id.includes(1) &&
                                                !["HOME_PROPERTY", "INVESTMENT_PROPERTY"].includes(option.key)
                                            ) {
                                                return option
                                            }
                                        })
                                        break
                                    case "financier":
                                        this.remoteOptions = res.data.map((option) => {
                                            return {
                                                label: option.label,
                                                value: option.label,
                                            }
                                        })
                                        break
                                }
                                break
                            case "asset_liability":
                                switch (this.field.field_mapping) {
                                    case "financier":
                                        this.remoteOptions = res.data.map((option) => {
                                            return {
                                                label: option.label,
                                                value: option.label,
                                            }
                                        })
                                        break
                                }
                                break
                            case "aai.other_income":
                                switch (this.field.field_mapping) {
                                    case "label":
                                        this.remoteOptions = res.data.filter((option) => {
                                            return !["Rental Income", "Spousal Offset Income"].includes(option.label)
                                        })
                                        break
                                }
                                break
                            case "investment_properties":
                                switch (this.field.field_mapping) {
                                    case "address.residential_status_id":
                                        this.remoteOptions = res.data.filter((option) => {
                                            return ["Owned Outright", "Mortgaged"].includes(option.label)
                                        })
                                        break
                                }
                                break
                        }
                    })
                    .catch((err) => {
                        console.error(err)
                    })
                    .finally(() => {
                        this.remoteLoading = false
                    })
            }
        },
    },

    watch: {
        selectValue: {
            handler(selectValue) {
                let path = String(this.path).split(".")

                if (this.field.field_mapping == "application_type_id") {
                    if (!_.get(this.formData.application, "loan_type_id", false)) {
                        _.set(this.formData.application, "loan_type_id", selectValue)
                    }
                }

                if (this.field.field_mapping == "loan_type_id") {
                    if (!_.get(this.formData.application, "application_type_id", false)) {
                        _.set(this.formData.application, "application_type_id", selectValue)
                    }
                }
                this.checkRules(this.field, this.path)
                this.setPathData(path, selectValue)
            },
        },
        value: {
            handler(value) {
                // prepopulate selected data
                if (this.isRemote && value) {
                    this.remoteCall(false, value)
                }

                if (_.isArray(value)) {
                    this.selectValue = !_.isEmpty(value) ? value : (this.isMulti ? [] : null)
                } else {
                    this.selectValue = value !== "" && !_.isNull(value) && typeof value != "undefined" ? String(value) : (this.isMulti ? [] : null)
                }

                if (_.isEmpty(this.selectValue)) {
                    this.selectValue = this.isMulti ? [] : null
                } else {
                    let selectValueType = typeof this.selectValue
                    switch (selectValueType) {
                        case 'string':
                            this.selectValue = this.isMulti ? [this.selectValue] : this.selectValue
                            break;
                        default:
                            break;
                    }
                }
            },
            immediate: true,
        },
    },
}
</script>

<style lang="scss" scoped>
.el-input :deep,
.el-select .el-input :deep {
    &.is-disabled {
        .el-input__inner {
            @apply fin-text-body #{!important};
        }
    }
    @apply fin-text-body;
}
</style>

<style lang="scss">
.el-select-dropdown {
    .el-scrollbar {
        .el-scrollbar__bar {
            @apply fin-opacity-100 #{!important};
        }
    }
}
</style>
